import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { LoaderService } from "../../../shared/components/loader/loader.service";
import { HeaderComponent } from "../../../shared/layout/app/header/header.component";
import { LeftSideNavComponent } from "../../../shared/layout/app/left-menu/left-side-nav.component";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import { UserService } from "../../../core/services/user.service";
import { ProfileBllService } from "../../profile/profile-bll.service";
import { ExhInviteBllService } from "../exh-invite-bll.service";
import { InviteDetailsModel } from "../exh-invite.model";
import { UserEventStatusRespModel } from "../../profile/profile.model";
import { APIErrorModel, BaseResponse } from "../../../shared/models/base-response.model";
import { Location } from "@angular/common";
import { HomeBllService } from "../../home/home-bll.service";
import { EventDetailsResponseModel } from "../../home/home.model";

/**
 * Component for displaying invite details and managing user event status.
 *
 * @component
 * @example
 * <app-invite-details></app-invite-details>
 */
@Component({
  selector: "app-invite-details",
  imports: [
    LeftSideNavComponent,
    HeaderComponent,
    CommonModule,
  ],
  templateUrl: "./invite-details.component.html",
  styleUrl: "./invite-details.component.scss",
})
export class InviteDetailsComponent implements OnInit {
  /**
   * Indicates if the component is currently loading data.
   */
  isLoading = true;

  /**
   * Indicates if the invite details are currently being loaded.
   */
  isInviteLoading = false;

  /**
   * Indicates if the user event status is currently being loaded.
   */
  isUserStatusLoading = false;

  /**
   * The details of the invite.
   */
  inviteDetails!: InviteDetailsModel;

  /**
   * Error message related to user event status.
   */
  userEventStatusError: string = "";

  /**
   * The status of the user's event.
   */
  userEventStatus: UserEventStatusRespModel | null = null;
  username: string = "";
  userLocation: string = "";
  inviteCode: string = "";

  eventDetails: EventDetailsResponseModel | null = null;
  
  /**
   * @param bll - The business logic layer service for handling invites.
   * @param router - The Angular router for navigation.
   * @param activatedRoute - The active route for retrieving route parameters.
   * @param loader - The service for managing loading indicators.
   * @param profileBll - The business logic layer service for handling user profiles.
   * @param userService - The service for managing user data.
   */
  constructor(
    private bll: ExhInviteBllService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private loader: LoaderService,
    private profileBll: ProfileBllService,
    private userService: UserService,
    private location: Location,
    private bllService: HomeBllService
  ) {}

  /**
   * Lifecycle hook that is called after the component has been initialized.
   * Fetches user event status and shows loader.
   */
  ngOnInit(): void {
    this.loader.show();

    this.activatedRoute.queryParams.subscribe((params) => {
      this.inviteCode = params["code"] || "";
      console.log("Invitation Code:", this.inviteCode);
    });

    if (this.inviteCode) {
      this.getInvitationDetails(this.inviteCode);
    }
    this.fetchUserEventStatus();

    this.loadEventDetails();
  }

  /**
   * Fetches the invitation details based on the provided invitation code.
   *
   * @param invitation_code - The invitation code to fetch details for.
   */
  getInvitationDetails(invitation_code: string) {
    this.isInviteLoading = true;
    this.updateLoadingState();
    this.bll
      .getInvitationDetails(invitation_code)
      .then((res) => this.handleInviteDeatailsResponse(res))
      .catch((err) => this.handleError(err))
      .finally(() => {
        this.isInviteLoading = false;
        this.updateLoadingState();
      });
  }

  /**
   * Updates the global loading state based on the status of both APIs.
   */
  private updateLoadingState() {
    this.isLoading = this.isInviteLoading || this.isUserStatusLoading;
    if (!this.isLoading) {
      this.loader.hide();
    }
  }

  /**
   * Handles the response from the invitation details API.
   *
   * @param res - The response from the API containing invite details.
   */
  async handleInviteDeatailsResponse(res: BaseResponse<InviteDetailsModel>) {
    if (res) {
      this.inviteDetails = res.data;
      const ticketCode = this.inviteDetails.encoded_ticket_code;
      if (ticketCode != null) {
        this.router.navigate(["/reg/ticket-details"], {
          queryParams: { code: ticketCode },
        });
      } else {
        this.username =
          this.inviteDetails.first_name + " " + this.inviteDetails.last_name;
        this.userLocation = [this.inviteDetails.city, this.inviteDetails.state]
          .filter((v) => v && v.trim() !== "")
          .join(", ");
      }
    } else {
      console.error("Error fetching ticket details");
    }
  }

  handleError(error: APIErrorModel) {
    if (error.status === 410){
      this.router.navigate(['/home']);
    }
  }

  /**
   * onImgError
   * Handles the image loading error scenario by replacing the source
   * with a default profile placeholder image.
   */
  onImgError(event: any): void {
    event.target.src = "assets/images/default-profile-individual.png";
  }

  /**
   * acceptInvitation
   * Accepts the current invitation using the provided invite code.
   * Displays a loader during the API request and navigates to ticket details
   * upon successful acceptance.
   */
  acceptInvitation(): void {
    this.loader.show();
    this.bll
      .acceptInvitation(this.inviteCode)
      .then((res) => {
        this.loader.hide();
        this.openTicketDetails(res.data.ticket_code);
      })
      .catch((err) => {
        this.loader.hide();
        // 👉 Handle 409 - Invitation Already Accepted
        if (err.status === 409) {
          this.router.navigate(["reg/my-tickets"]);
        }
      });
  }

  /**
   * openTicketDetails
   * Navigates the user to the ticket details page after invitation acceptance.
   */
  openTicketDetails(ticketCode: string): void {
    console.log("Opening ticket details for ticket code:", ticketCode);
    this.bll.saveInviteAccepted("accepted");

    // Navigate to ticket details page with query param
    this.router.navigate(["/reg/ticket-details"], {
      queryParams: { code: ticketCode },
    });
  }

  /**
   * Fetches the user event status and updates the user service with the profile information.
   */
  fetchUserEventStatus(): void {
    this.isUserStatusLoading = true;
    this.updateLoadingState();
    this.profileBll
      .submitGetUserEventStatus()
      .then((res) => {
        if (res !== null) {
          this.userEventStatusError = "";

          this.userEventStatus = res.data;
          let previewLogo =
            this.userEventStatus.user_profile.image_url ??
            "/assets/images/Default-profile-photo.png";
          let userName =
            this.userEventStatus.user_profile.first_name +
            " " +
            this.userEventStatus.user_profile.last_name;
          this.userService.setUser({
            name: userName, // get from API or JWT
            profileImg: previewLogo, // fallback if empty
            mobile: this.userEventStatus.user_profile.mobile,
            state: this.userEventStatus.user_profile.state,
            city: this.userEventStatus.user_profile.city,
            hasUnassignedTickets:
              res.data.event_my_tickets.unassigned_tickets_bought_by_me > 0,
            hasAssignedTickets: res.data.event_my_tickets.assigned_tickets_bought_by_me > 0 || res.data.event_my_tickets.tickets_assigned_for_me > 0,
          });
        }
      })
      .catch((err: any) => {
        this.userEventStatusError =
          err?.error?.message ||
          err?.message ||
          "Error fetching user event status.";
        console.error(
          "Error fetching user event status:",
          err?.error?.message || err?.message || err
        );
      })
      .finally(() => {
        this.isUserStatusLoading = false;
        this.updateLoadingState();
      });
  }
  navigateToList(): void {
    this.location.back();
  }

  loadEventDetails() {
    this.bllService.fetchEventDetails()
      .then((response) => {
        this.eventDetails = response.data;     
        this.userService.setLeftMenu({
          allow_to_register: this.eventDetails.allow_to_register,
          show_digital_catalog: this.eventDetails.show_digital_catalog,
          event_status: this.eventDetails.event_status
        });       
      })
      .catch((error) => {
        console.error('Error fetching event details:', error);
      });
  }

}
